import { convertToUtc } from "@gigpro/utils/datetime";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { produce } from "immer";
import type { PatchedShiftBreakWriteRequest } from "../../generated/model/patchedShiftBreakWriteRequest";
import type { ShiftBreakClaimOverrideRequest } from "../../generated/model/shiftBreakClaimOverrideRequest";
import type { ShiftBreakClaimRead } from "../../generated/model/shiftBreakClaimRead";
import type { ShiftBreakClaimWriteRequest } from "../../generated/model/shiftBreakClaimWriteRequest";
import type { ShiftBreakWrite } from "../../generated/model/shiftBreakWrite";
import type { ShiftBreakWriteRequest } from "../../generated/model/shiftBreakWriteRequest";
import type { TimePunchCreate } from "../../generated/model/timePunchCreate";
import type { TimePunchCreateRequest } from "../../generated/model/timePunchCreateRequest";
import type { TimePunchVerifyRequestRequest } from "../../generated/model/timePunchVerifyRequestRequest";
import {
  timesheetsBreakClaimsCreate,
  timesheetsBreakClaimsOverrideCreate,
  timesheetsShiftBreaksCreate,
  timesheetsShiftBreaksPartialUpdate,
  timesheetsTimePunchesCreate,
  timesheetsTimePunchesVerifyCreate,
} from "../../generated/timesheets/timesheets";
import type { Tz } from "../types";

interface TimePunchCreateRequestWithTransform extends TimePunchCreateRequest, Tz {}

const applyTzToTimePunch = <T extends Tz & { time: Date }>(request: T): T =>
  produce(request, (draft) => {
    if (draft.timezone) draft.time = convertToUtc(draft.time, draft.timezone);
    delete draft.timezone;
  });

export const timesheetsTimePunchesCreateWithTransform = (data: TimePunchCreateRequestWithTransform) =>
  timesheetsTimePunchesCreate(applyTzToTimePunch(data));

interface TimePunchVerifyWithTransformVariables extends TimePunchVerifyRequestRequest, Tz {
  id: string;
}

export const timesheetsTimePunchesVerifyCreateWithTransform = ({
  id,
  ...data
}: TimePunchVerifyWithTransformVariables) => timesheetsTimePunchesVerifyCreate(id, applyTzToTimePunch(data));

export function useTimesheetsTimePunchesCreateWithTransform(options?: {
  mutation?: Omit<UseMutationOptions<TimePunchCreate, unknown, TimePunchCreateRequestWithTransform>, "mutationFn">;
}) {
  return useMutation({ ...options?.mutation, mutationFn: timesheetsTimePunchesCreateWithTransform });
}

interface ShiftBreakWriteRequestWithTransform extends ShiftBreakWriteRequest, Tz {}
interface PatchedShiftBreakWriteRequestWithTransform extends PatchedShiftBreakWriteRequest, Tz {}

const applyTzToShiftBreak = <T extends Tz & { start_time?: Date | null; end_time?: Date | null }>(request: T): T =>
  produce(request, (draft) => {
    if (draft.timezone) {
      if (draft.start_time) draft.start_time = convertToUtc(draft.start_time, draft.timezone);
      if (draft.end_time) draft.end_time = convertToUtc(draft.end_time, draft.timezone);
    }
    delete draft.timezone;
  });

/** POST always sends `create_new=true` — baked into the request path. */
export const timesheetsShiftBreaksCreateWithTransform = (data: ShiftBreakWriteRequestWithTransform) =>
  timesheetsShiftBreaksCreate(applyTzToShiftBreak(data), { create_new: true });

export function useTimesheetsShiftBreaksCreateWithTransform(options?: {
  mutation?: Omit<UseMutationOptions<ShiftBreakWrite, unknown, ShiftBreakWriteRequestWithTransform>, "mutationFn">;
}) {
  return useMutation({ ...options?.mutation, mutationFn: timesheetsShiftBreaksCreateWithTransform });
}

type ShiftBreaksPartialUpdateWithTransformVariables = { id: string; data: PatchedShiftBreakWriteRequestWithTransform };

export const timesheetsShiftBreaksPartialUpdateWithTransform = ({
  id,
  data,
}: ShiftBreaksPartialUpdateWithTransformVariables) => timesheetsShiftBreaksPartialUpdate(id, applyTzToShiftBreak(data));

export function useTimesheetsShiftBreaksPartialUpdateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<ShiftBreakWrite, unknown, ShiftBreaksPartialUpdateWithTransformVariables>,
    "mutationFn"
  >;
}) {
  return useMutation({ ...options?.mutation, mutationFn: timesheetsShiftBreaksPartialUpdateWithTransform });
}

interface ShiftBreakClaimWriteRequestWithTransform extends ShiftBreakClaimWriteRequest, Tz {}

/** Converts every row back to UTC. */
export const timesheetsBreakClaimsCreateWithTransform = ({
  timezone,
  ...data
}: ShiftBreakClaimWriteRequestWithTransform) =>
  timesheetsBreakClaimsCreate({
    ...data,
    rows: data.rows.map((row) => applyTzToShiftBreak({ ...row, timezone })),
  });

interface ShiftBreakClaimOverrideRequestWithTransform extends ShiftBreakClaimOverrideRequest, Tz {}

/** Converts every row back to UTC. */
export const timesheetsBreakClaimsOverrideCreateWithTransform = ({
  timezone,
  ...data
}: ShiftBreakClaimOverrideRequestWithTransform) =>
  timesheetsBreakClaimsOverrideCreate({
    ...data,
    rows: data.rows.map((row) => applyTzToShiftBreak({ ...row, timezone })),
  });

export function useTimesheetsBreakClaimsOverrideCreateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<Array<ShiftBreakClaimRead>, unknown, ShiftBreakClaimOverrideRequestWithTransform>,
    "mutationFn"
  >;
}) {
  return useMutation({ ...options?.mutation, mutationFn: timesheetsBreakClaimsOverrideCreateWithTransform });
}
